import React, { useState } from "react";
import Seo from "@/components/Seo";
import { LifeBuoy, ShieldAlert, FileText } from "lucide-react";
import SupportChat from "@/components/support/SupportChat";
import EscalationForm from "@/components/support/EscalationForm";

export default function TechnicalSupport() {
  const [escalating, setEscalating] = useState(null);

  return (
    <>
      <Seo
        title="EI Technical Support — product help for installers | EI Battery Solutions"
        description="Ask our AI technical assistant about Dyness, Solis, Solinteg and Risen products. Every answer is cited from the approved manufacturer manuals, datasheets and compatibility documents we hold."
        canonical="https://eibattery.com.au/support"
      />

      <section className="bg-ei-navy text-ei-white">
        <div className="max-w-5xl mx-auto px-5 sm:px-8 py-12 sm:py-16">
          <div className="inline-flex items-center gap-2 text-ei-gold text-xs font-display font-bold uppercase tracking-[0.2em]">
            <LifeBuoy className="w-4 h-4" /> Technical support
          </div>
          <h1 className="mt-4 font-display text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.05]">
            EI Technical Support
          </h1>
          <p className="mt-5 text-ei-white/75 text-base max-w-2xl leading-relaxed">
            Our AI technical assistant answers from the manufacturer manuals, datasheets and compatibility documents we
            hold, and shows you the document and page behind every answer. Anything it cannot evidence goes to a human
            specialist.
          </p>
        </div>
      </section>

      <section className="max-w-5xl mx-auto px-5 sm:px-8 py-10 sm:py-14">
        <div className="grid lg:grid-cols-[1fr_300px] gap-8">
          <div className="bg-white border border-ei-mist rounded-md p-4 sm:p-6 min-h-[560px] flex flex-col" data-testid="support-page-chat">
            {escalating ? (
              <EscalationForm product={escalating.product} sessionId={escalating.sessionId} onDone={() => setEscalating(null)} />
            ) : (
              <SupportChat onRequestEscalation={(product) => setEscalating({ product, sessionId: null })} />
            )}
          </div>

          <aside className="space-y-4">
            <div className="bg-white border border-ei-mist rounded-md p-5">
              <div className="flex items-center gap-2 text-ei-navy font-display text-sm font-bold">
                <FileText className="w-4 h-4 text-ei-gold" /> What it can help with
              </div>
              <ul className="mt-3 space-y-2 text-sm text-ei-graphite/80">
                <li>Published product specifications</li>
                <li>Finding the right manual or datasheet</li>
                <li>Documented inverter and battery compatibility</li>
                <li>What a displayed fault code means</li>
                <li>Passing the question to our technical team</li>
              </ul>
              <a
                href="/support/fault-codes"
                className="mt-4 inline-flex items-center gap-2 font-display text-xs font-bold text-ei-navy border border-ei-mist px-4 py-2.5 rounded-sm hover:border-ei-navy min-h-[44px]"
                data-testid="support-fault-code-link"
              >
                <ShieldAlert className="w-3.5 h-3.5" /> Fault code lookup
              </a>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-md p-5" data-testid="support-safety-notice">
              <div className="flex items-center gap-2 text-amber-900 font-display text-sm font-bold">
                <ShieldAlert className="w-4 h-4" /> Safety first
              </div>
              <p className="mt-2 text-sm text-amber-900/90 leading-relaxed">
                Installation, wiring, commissioning and electrical testing must be carried out by a licensed electrical
                contractor. If equipment is smoking, swelling or dangerously hot, move away from it and call 000.
              </p>
            </div>
          </aside>
        </div>
      </section>
    </>
  );
}
